import {
  CreateGrantApiError,
  CreateImpersonationGrantRequest,
  GetImpersonationGrantsParams,
  GrantWithSessions,
  ImpersonationGrantsResult,
  ImpersonationSessionActivity,
  MeResponse,
} from '../../features/CommonSettings/Impersonation/types'
import { apiSlice } from './apiQuery'

// E11 R1 (WI 611) — the platform Global-Admin impersonation grants surface. Injected on the shared slice as
// domain 'User' (routed to the same-origin USERS API by fetchData). getMe is the route guard's source of
// isGlobalAdmin; the grants list is tagged 'ImpersonationGrants' and create/revoke invalidate it so the table
// refetches. Every endpoint is dark until the E11 cut-over. All ids/reason come from the caller server-side
// where they must (grantor is never on the wire); the SPA only sends grantee/target/reason/expiresAt.
export const apiImpersonation = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    // The session-aware GET /api/v1/me. R1 uses only isGlobalAdmin (route guard) + activePersona.id (default
    // grantee = self). Not tagged — it is fetched once for the guard, not part of the grants cache graph.
    getMe: build.query<MeResponse, void>({
      query: () => ({ url: 'v1/me', method: 'GET', domain: 'User' }),
    }),

    // GET v1/impersonation/grants — the full grants list (any tenant). Optional status(CSV)/tenantId/granteeId
    // filters drive the query string. Returns up to 500 rows + a hasMore cap flag.
    getImpersonationGrants: build.query<ImpersonationGrantsResult, GetImpersonationGrantsParams | void>({
      query: (args) => {
        const { status, tenantId, granteeId } = args ?? {}
        const params: Record<string, string | number> = {}
        if (status) params.status = status
        if (tenantId != null) params.tenantId = tenantId
        if (granteeId != null) params.granteeId = granteeId
        return {
          url: 'v1/impersonation/grants',
          method: 'GET',
          domain: 'User',
          params: Object.keys(params).length ? params : undefined,
        }
      },
      providesTags: () => [{ type: 'ImpersonationGrants', id: 'ImpersonationGrants' }],
    }),

    // POST v1/impersonation/grants — create a grant. Returns the new grant id (the envelope is unwrapped by
    // fetchData). transformErrorResponse turns the 400 INVALID_GRANT_REQUEST envelope into a typed result; the
    // 8-hour ceiling (sproc THROW 50204) is detected by the message so the modal can render it inline.
    createImpersonationGrant: build.mutation<number, CreateImpersonationGrantRequest>({
      query: (body) => ({ url: 'v1/impersonation/grants', method: 'POST', domain: 'User', body }),
      invalidatesTags: () => [{ type: 'ImpersonationGrants', id: 'ImpersonationGrants' }],
      transformErrorResponse: (resp): CreateGrantApiError => {
        // The base query returns { status, data } where data is the ResponseDto envelope { data: { code,
        // message } } (or a bare string on a network error).
        const r = resp as { status?: number; data?: any }
        const body = r?.data?.data ?? r?.data
        const code: string | undefined = body?.code
        const message: string | undefined = body?.message
        if (r.status === 400 && code === 'INVALID_GRANT_REQUEST') {
          if (message && /8-hour/i.test(message)) {
            return { kind: 'exceeds-max-duration', message }
          }
          return { kind: 'invalid-grant', message: message ?? '' }
        }
        return { kind: 'unknown', status: r.status, message: message ?? '' }
      },
    }),

    // POST v1/impersonation/grants/{id}/revoke — revoke a grant (force-ends its session). 204 no body.
    revokeImpersonationGrant: build.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `v1/impersonation/grants/${id}/revoke`,
        method: 'POST',
        domain: 'User',
      }),
      invalidatesTags: () => [{ type: 'ImpersonationGrants', id: 'ImpersonationGrants' }],
    }),

    // POST v1/impersonation/sessions/active/end — end the CALLER's own active session (the server resolves
    // it from the acting persona; no id on the wire, no step-up). This is the ImpersonationBanner's Stop
    // button and its auto-end-at-expiry path (R2), and the sign-out coordinator's pre-logout end (R4). 204
    // no body. Invalidates the grants list so the admin grants page reflects the now-ended session; the
    // banner itself hard-reloads to the app root on success (ending impersonation flips the whole request
    // identity, so all impersonated-context cache must be purged, not surgically invalidated).
    endActiveImpersonationSession: build.mutation<void, void>({
      query: () => ({ url: 'v1/impersonation/sessions/active/end', method: 'POST', domain: 'User' }),
      invalidatesTags: () => [{ type: 'ImpersonationGrants', id: 'ImpersonationGrants' }],
    }),

    // POST v1/impersonation/sessions — start a session under a grant (R3, WI 605). Returns the new session
    // Id (the endpoint's OK body is a bare int; fetchData unwraps the ResponseDto envelope). Requires step-up
    // MFA (authContext c1): if the caller's token lacks it the server replies 401 with an RFC-9470
    // WWW-Authenticate claims challenge, which the shared axios response interceptor answers with an
    // acquireTokenRedirect — useImpersonationStart persists the pending grant across that redirect and
    // resumes the POST on return. Invalidates the grants list (the started grant flips to 'Used').
    startImpersonationSession: build.mutation<number, { grantId: number }>({
      query: ({ grantId }) => ({
        url: 'v1/impersonation/sessions',
        method: 'POST',
        domain: 'User',
        body: { grantId },
      }),
      invalidatesTags: () => [{ type: 'ImpersonationGrants', id: 'ImpersonationGrants' }],
    }),

    // POST v1/impersonation/sessions/end-all — break-glass platform-wide terminate of ALL active sessions
    // (R5, WI 619). Returns the ended count. The server re-validates the exact confirmPhrase
    // ("END-ALL-IMPERSONATION", case-sensitive) and 400s a wrong value before any write — the SPA gates the
    // same phrase client-side purely as friction. Requires step-up MFA (c1): a token lacking it 401s with a
    // claims challenge the shared interceptor answers by redirect (no replay — the admin re-confirms after
    // MFA). Invalidates the grants list so any 'Used' rows flip.
    endAllImpersonationSessions: build.mutation<number, { confirmPhrase: string }>({
      query: ({ confirmPhrase }) => ({
        url: 'v1/impersonation/sessions/end-all',
        method: 'POST',
        domain: 'User',
        body: { confirmPhrase },
      }),
      invalidatesTags: () => [{ type: 'ImpersonationGrants', id: 'ImpersonationGrants' }],
    }),

    // GET v1/impersonation/grants/{id}/sessions — one grant + its sessions (oldest-first), for the grants
    // row-expansion (R5, WI 612). Fetched lazily when a row is expanded (the panel skips until then). Tagged
    // with the SHARED ImpersonationGrants id (the one create/revoke/start/end/end-all all invalidate) so an
    // open panel refetches after any write — a per-grant tag would be stale, since no mutation invalidates it.
    getGrantWithSessions: build.query<GrantWithSessions, number>({
      query: (grantId) => ({ url: `v1/impersonation/grants/${grantId}/sessions`, method: 'GET', domain: 'User' }),
      providesTags: () => [{ type: 'ImpersonationGrants', id: 'ImpersonationGrants' }],
    }),

    // GET v1/impersonation/events/count — count of sessions started in the last N days, for the page badge
    // (R5, WI 612). Tagged so it refreshes when the grants list is invalidated (a start/revoke changes it).
    getImpersonationEventCount: build.query<number, { days?: number } | void>({
      query: (args) => {
        const days = args?.days
        return {
          url: 'v1/impersonation/events/count',
          method: 'GET',
          domain: 'User',
          params: days != null ? { days } : undefined,
        }
      },
      providesTags: () => [{ type: 'ImpersonationGrants', id: 'ImpersonationGrants' }],
    }),

    // GET v1/impersonation/sessions/{sessionId}/activity — the recent audited writes attributed to a session,
    // for the banner's "View session activity" drawer (R5, WI 616). Fetched only while the drawer is open
    // (the drawer passes { skip: !open }); the drawer's Refresh button calls refetch(). Deliberately NOT
    // tagged into the ImpersonationGrants cache graph — it is a transient, per-session read, not part of the
    // grants list's invalidation set.
    getSessionActivity: build.query<ImpersonationSessionActivity[], { sessionId: number; top?: number }>({
      query: ({ sessionId, top }) => ({
        url: `v1/impersonation/sessions/${sessionId}/activity`,
        method: 'GET',
        domain: 'User',
        params: top != null ? { top } : undefined,
      }),
    }),
  }),
})

export const {
  useGetMeQuery,
  useGetImpersonationGrantsQuery,
  useCreateImpersonationGrantMutation,
  useRevokeImpersonationGrantMutation,
  useEndActiveImpersonationSessionMutation,
  useStartImpersonationSessionMutation,
  useEndAllImpersonationSessionsMutation,
  useGetGrantWithSessionsQuery,
  useGetImpersonationEventCountQuery,
  useGetSessionActivityQuery,
} = apiImpersonation
